import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type RootState } from './index';

export interface MaterialGrade {
  value: string;
  category: string;
}

const SEED_MATERIAL_GRADES: MaterialGrade[] = [];

export interface LoadingPoint {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  isSelected: boolean;
  loadingDurationMins: number;
  materialGrade: string;
  loadingCapacity: number;
  location?: string;
  gradeId?: string;
  gradeLocationId?: string;
  materialGradeValue?: number;
}

export interface DumpingPoint {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  isSelected: boolean;
  dumpingDurationMins: number;
  blendRatios: number[];
  dumpingCapacity: number;
  location?: string;
  locationId?: string;
  targetBlend?: number;
}

export interface SiteTravelTime {
  loadingSiteId: string;
  dumpingSiteId: string;
  loadedMins: number;
  emptyMins: number;
}

export interface RouteSegment {
  edge: string;
  routeId: string;
  minutes: number;
  geohashes: string[];
  isReversed: boolean;
}

export interface RoutePath {
  totalMinutes: number;
  segments: RouteSegment[];
}

export interface RouteResult {
  fromLocationId: string;
  toLocationId: string;
  from: string;
  to: string;
  paths: RoutePath[];
}

export interface PairState {
  from: string;
  to: string;
  fromLocationId: string;
  toLocationId: string;
  selectedPathIndex: number;
  segments: Array<{
    routeId: string;
    edge: string;
    minutes: number;
    isReversed: boolean;
    geohashes: string[];
  }>;
  totalMinutes: number;
}

interface SiteConfigSnapshot {
  loadingPoints: LoadingPoint[];
  dumpingPoints: DumpingPoint[];
  travelTimes: SiteTravelTime[];
  materialGrades: MaterialGrade[];
}

export interface GradeApiItem {
  gradeId: string;
  name: string;
  materialCategory: string | null;
  locationName: string;
  locationId: string;
  inventory: number;
  qualities: Record<string, number>;
}

export interface SiteConfigState {
  loadingPoints: LoadingPoint[];
  dumpingPoints: DumpingPoint[];
  travelTimes: SiteTravelTime[];
  materialGrades: MaterialGrade[];
  committedSnapshot: SiteConfigSnapshot | null;
  routeResults: RouteResult[];
  pairStates: PairState[];
  availableGrades: GradeApiItem[];
  productionTarget: number;
  excessIdling: string;
  lunchTolerance: string;
  minimalFleetCount: number | null;
}

const initialState: SiteConfigState = {
  loadingPoints: [],
  dumpingPoints: [],
  travelTimes: [],
  materialGrades: SEED_MATERIAL_GRADES,
  committedSnapshot: null,
  routeResults: [],
  pairStates: [],
  availableGrades: [],
  productionTarget: 0,
  excessIdling: '0',
  lunchTolerance: '0',
  minimalFleetCount: null,
};

function regenerateTravelTimesInPlace(state: SiteConfigState): void {
  const existing = new Set(
    state.travelTimes.map((tt) => `${tt.loadingSiteId}-${tt.dumpingSiteId}`)
  );
  state.loadingPoints.forEach((lp) => {
    state.dumpingPoints.forEach((dp) => {
      const key = `${lp.id}-${dp.id}`;
      if (!existing.has(key)) {
        state.travelTimes.push({
          loadingSiteId: lp.id,
          dumpingSiteId: dp.id,
          loadedMins: 0,
          emptyMins: 0,
        });
        existing.add(key);
      }
    });
  });
}

function nextLoadingId(points: LoadingPoint[]): string {
  const max = points.reduce((m, p) => {
    const n = parseInt(p.id.replace('L', ''), 10);
    return isNaN(n) ? m : Math.max(m, n);
  }, -1);
  return `L${max + 1}`;
}

function nextDumpingId(points: DumpingPoint[]): string {
  const max = points.reduce((m, p) => {
    const n = parseInt(p.id.replace('D', ''), 10);
    return isNaN(n) ? m : Math.max(m, n);
  }, -1);
  return `D${max + 1}`;
}

export const siteConfigSlice = createSlice({
  name: 'siteConfig',
  initialState,
  reducers: {
    addLoadingPoint(state, action: PayloadAction<Omit<LoadingPoint, 'id'>>) {
      const id = nextLoadingId(state.loadingPoints);
      state.loadingPoints.push({ ...action.payload, id });
      regenerateTravelTimesInPlace(state);
    },
    editLoadingPoint(state, action: PayloadAction<LoadingPoint>) {
      const idx = state.loadingPoints.findIndex(
        (p) => p.id === action.payload.id
      );
      if (idx !== -1) state.loadingPoints[idx] = action.payload;
    },
    addDumpingPoint(state, action: PayloadAction<Omit<DumpingPoint, 'id'>>) {
      const id = nextDumpingId(state.dumpingPoints);
      state.dumpingPoints.push({ ...action.payload, id });
      regenerateTravelTimesInPlace(state);
    },
    editDumpingPoint(state, action: PayloadAction<DumpingPoint>) {
      const idx = state.dumpingPoints.findIndex(
        (p) => p.id === action.payload.id
      );
      if (idx !== -1) state.dumpingPoints[idx] = action.payload;
    },
    updateTravelTime(
      state,
      action: PayloadAction<{
        loadingSiteId: string;
        dumpingSiteId: string;
        loadedMins: number;
        emptyMins: number;
      }>
    ) {
      const { loadingSiteId, dumpingSiteId, loadedMins, emptyMins } =
        action.payload;
      const idx = state.travelTimes.findIndex(
        (tt) =>
          tt.loadingSiteId === loadingSiteId &&
          tt.dumpingSiteId === dumpingSiteId
      );
      if (idx !== -1) {
        state.travelTimes[idx] = {
          loadingSiteId,
          dumpingSiteId,
          loadedMins,
          emptyMins,
        };
      } else {
        state.travelTimes.push({
          loadingSiteId,
          dumpingSiteId,
          loadedMins,
          emptyMins,
        });
      }
    },
    commitConfig(state) {
      state.committedSnapshot = {
        loadingPoints: JSON.parse(JSON.stringify(state.loadingPoints)),
        dumpingPoints: JSON.parse(JSON.stringify(state.dumpingPoints)),
        travelTimes: JSON.parse(JSON.stringify(state.travelTimes)),
        materialGrades: JSON.parse(JSON.stringify(state.materialGrades)),
      };
    },
    regenerateTravelTimes(state) {
      regenerateTravelTimesInPlace(state);
    },
    loadSiteConfigFromAPI(
      state,
      action: PayloadAction<{
        loadingPoints: Array<
          Omit<LoadingPoint, 'isSelected'> & { isSelected?: boolean }
        >;
        dumpingPoints: Array<
          Omit<DumpingPoint, 'isSelected'> & { isSelected?: boolean }
        >;
        travelTimes: SiteTravelTime[];
        materialGrades?: MaterialGrade[];
      }>
    ) {
      state.loadingPoints = action.payload.loadingPoints.map((lp) => ({
        ...lp,
        isSelected: lp.isSelected ?? true,
      }));
      state.dumpingPoints = action.payload.dumpingPoints.map((dp) => ({
        ...dp,
        isSelected: dp.isSelected ?? true,
      }));
      state.travelTimes = action.payload.travelTimes;
      if (
        action.payload.materialGrades &&
        action.payload.materialGrades.length > 0
      ) {
        state.materialGrades = action.payload.materialGrades;
      }
      state.committedSnapshot = null;
    },
    removeLoadingPoint(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.loadingPoints = state.loadingPoints.filter((lp) => lp.id !== id);
      state.travelTimes = state.travelTimes.filter(
        (tt) => tt.loadingSiteId !== id
      );
    },
    removeDumpingPoint(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.dumpingPoints = state.dumpingPoints.filter((dp) => dp.id !== id);
      state.travelTimes = state.travelTimes.filter(
        (tt) => tt.dumpingSiteId !== id
      );
    },
    syncFromInputStep(
      state,
      action: PayloadAction<{
        loadingPoints: Array<{
          id: string;
          loadingDurationMins: number;
          loadingCapacity: number;
        }>;
        dumpingPoints: Array<{
          id: string;
          dumpingDurationMins: number;
          blendRatios: number[];
          dumpingCapacity: number;
        }>;
        travelTimes: Array<{
          loadingSiteId: string;
          dumpingSiteId: string;
          loadedMins: number;
          emptyMins: number;
        }>;
      }>
    ) {
      action.payload.loadingPoints.forEach((update) => {
        const idx = state.loadingPoints.findIndex((lp) => lp.id === update.id);
        if (idx !== -1) {
          state.loadingPoints[idx].loadingDurationMins =
            update.loadingDurationMins;
          state.loadingPoints[idx].loadingCapacity = update.loadingCapacity;
        }
      });
      action.payload.dumpingPoints.forEach((update) => {
        const idx = state.dumpingPoints.findIndex((dp) => dp.id === update.id);
        if (idx !== -1) {
          state.dumpingPoints[idx].dumpingDurationMins =
            update.dumpingDurationMins;
          state.dumpingPoints[idx].blendRatios = update.blendRatios;
          state.dumpingPoints[idx].dumpingCapacity = update.dumpingCapacity;
        }
      });
      action.payload.travelTimes.forEach((update) => {
        const idx = state.travelTimes.findIndex(
          (tt) =>
            tt.loadingSiteId === update.loadingSiteId &&
            tt.dumpingSiteId === update.dumpingSiteId
        );
        if (idx !== -1) {
          state.travelTimes[idx].loadedMins = update.loadedMins;
          state.travelTimes[idx].emptyMins = update.emptyMins;
        }
      });
    },
    hydrateSiteDataFromAPI(
      state,
      action: PayloadAction<{
        travelTimes: Array<{
          loadingSiteId: string;
          dumpingSiteId: string;
          loadedMins: number;
          emptyMins: number;
        }>;
        loadingDurations: Array<{ id: string; durationMins: number }>;
        dumpingDurations: Array<{ id: string; durationMins: number }>;
      }>
    ) {
      if (state.committedSnapshot !== null) return;

      action.payload.travelTimes.forEach(
        ({ loadingSiteId, dumpingSiteId, loadedMins, emptyMins }) => {
          const idx = state.travelTimes.findIndex(
            (tt) =>
              tt.loadingSiteId === loadingSiteId &&
              tt.dumpingSiteId === dumpingSiteId
          );
          if (idx !== -1) {
            state.travelTimes[idx].loadedMins = loadedMins;
            state.travelTimes[idx].emptyMins = emptyMins;
          }
        }
      );

      action.payload.loadingDurations.forEach(({ id, durationMins }) => {
        const idx = state.loadingPoints.findIndex((lp) => lp.id === id);
        if (idx !== -1)
          state.loadingPoints[idx].loadingDurationMins = durationMins;
      });

      action.payload.dumpingDurations.forEach(({ id, durationMins }) => {
        const idx = state.dumpingPoints.findIndex((dp) => dp.id === id);
        if (idx !== -1)
          state.dumpingPoints[idx].dumpingDurationMins = durationMins;
      });
    },
    setSiteOperationalInputs(
      state,
      action: PayloadAction<{
        productionTarget: number;
        excessIdling: string;
        lunchTolerance: string;
      }>
    ) {
      state.productionTarget = action.payload.productionTarget;
      state.excessIdling = action.payload.excessIdling;
      state.lunchTolerance = action.payload.lunchTolerance;
    },
    setMinimalFleetCount(state, action: PayloadAction<number | null>) {
      state.minimalFleetCount = action.payload;
    },
    addMaterialGrade(state, action: PayloadAction<MaterialGrade>) {
      state.materialGrades.push(action.payload);
    },
    setAvailableGrades(state, action: PayloadAction<GradeApiItem[]>) {
      state.availableGrades = action.payload;
    },
    setRouteResults(state, action: PayloadAction<RouteResult[]>) {
      state.routeResults = action.payload;
      state.pairStates = action.payload.map((r) => ({
        from: r.from,
        to: r.to,
        fromLocationId: r.fromLocationId,
        toLocationId: r.toLocationId,
        selectedPathIndex: 0,
        segments: r.paths[0]?.segments.map((s) => ({ ...s })) ?? [],
        totalMinutes: r.paths[0]?.totalMinutes ?? 0,
      }));
    },
    updatePairPathIndex(
      state,
      action: PayloadAction<{
        from: string;
        to: string;
        selectedPathIndex: number;
      }>
    ) {
      const { from, to, selectedPathIndex } = action.payload;
      const pairIdx = state.pairStates.findIndex(
        (ps) => ps.from === from && ps.to === to
      );
      if (pairIdx === -1) return;
      state.pairStates[pairIdx].selectedPathIndex = selectedPathIndex;
      const routeResult = state.routeResults.find(
        (r) => r.from === from && r.to === to
      );
      const path = routeResult?.paths[selectedPathIndex];
      if (path) {
        state.pairStates[pairIdx].segments = path.segments.map((s) => ({
          ...s,
        }));
        state.pairStates[pairIdx].totalMinutes = path.totalMinutes;
      }
    },
    updatePairSegmentMinutes(
      state,
      action: PayloadAction<{
        from: string;
        to: string;
        segmentIndex: number;
        minutes: number;
      }>
    ) {
      const { from, to, segmentIndex, minutes } = action.payload;
      const pairIdx = state.pairStates.findIndex(
        (ps) => ps.from === from && ps.to === to
      );
      if (pairIdx === -1) return;
      state.pairStates[pairIdx].segments[segmentIndex].minutes = minutes;
      state.pairStates[pairIdx].totalMinutes = state.pairStates[
        pairIdx
      ].segments.reduce((sum, s) => sum + s.minutes, 0);
    },
  },
});

export const {
  addLoadingPoint,
  editLoadingPoint,
  addDumpingPoint,
  editDumpingPoint,
  updateTravelTime,
  commitConfig,
  regenerateTravelTimes,
  hydrateSiteDataFromAPI,
  syncFromInputStep,
  removeLoadingPoint,
  removeDumpingPoint,
  loadSiteConfigFromAPI,
  addMaterialGrade,
  setRouteResults,
  setAvailableGrades,
  updatePairPathIndex,
  updatePairSegmentMinutes,
  setSiteOperationalInputs,
  setMinimalFleetCount,
} = siteConfigSlice.actions;

export const selectDispatchPayload = (state: RootState) => {
  const { loadingPoints, dumpingPoints, travelTimes } = state.siteConfigReducer;

  const loadingDurations: Record<string, number> = {};
  const loadingCapacities: Record<string, number> = {};
  const dumpingDurations: Record<string, number> = {};
  const dumpingCapacities: Record<string, number> = {};
  const blendRatios: Record<string, number[]> = {};
  const travelTimesPayload: Record<string, number> = {};

  loadingPoints.forEach((lp) => {
    loadingDurations[lp.id] = lp.loadingDurationMins;
    loadingCapacities[lp.id] = lp.loadingCapacity;
  });

  dumpingPoints.forEach((dp) => {
    dumpingDurations[dp.id] = dp.dumpingDurationMins;
    dumpingCapacities[dp.id] = dp.dumpingCapacity;
    blendRatios[dp.id] = dp.blendRatios;
  });

  travelTimes.forEach((tt) => {
    travelTimesPayload[`${tt.loadingSiteId}-${tt.dumpingSiteId}-loaded`] =
      tt.loadedMins;
    travelTimesPayload[`${tt.loadingSiteId}-${tt.dumpingSiteId}-empty`] =
      tt.emptyMins;
  });

  return {
    loadingDurations,
    dumpingDurations,
    blendRatios,
    loadingCapacities,
    dumpingCapacities,
    travelTimes: travelTimesPayload,
  };
};

export const selectHasUnsavedChanges = (state: RootState): boolean => {
  const {
    loadingPoints,
    dumpingPoints,
    travelTimes,
    materialGrades,
    committedSnapshot,
  } = state.siteConfigReducer;
  if (!committedSnapshot) return false;
  return (
    JSON.stringify({
      loadingPoints,
      dumpingPoints,
      travelTimes,
      materialGrades,
    }) !== JSON.stringify(committedSnapshot)
  );
};

export const selectActiveLoadingPoints = (state: RootState) =>
  state.siteConfigReducer.loadingPoints.filter(
    (lp) => lp.isActive && lp.isSelected
  );

export const selectActiveDumpingPoints = (state: RootState) =>
  state.siteConfigReducer.dumpingPoints.filter(
    (dp) => dp.isActive && dp.isSelected
  );

export const selectActiveSelectedCombinations = (state: RootState) => {
  const { loadingPoints, dumpingPoints, travelTimes } = state.siteConfigReducer;
  const activeLoading = loadingPoints.filter(
    (lp) => lp.isActive && lp.isSelected
  );
  const activeDumping = dumpingPoints.filter(
    (dp) => dp.isActive && dp.isSelected
  );
  return activeLoading.flatMap((lp) =>
    activeDumping.map((dp) => ({
      loading: lp,
      dumping: dp,
      travelTime: travelTimes.find(
        (tt) => tt.loadingSiteId === lp.id && tt.dumpingSiteId === dp.id
      ) ?? {
        loadingSiteId: lp.id,
        dumpingSiteId: dp.id,
        loadedMins: 0,
        emptyMins: 0,
      },
    }))
  );
};

export const selectMaterialGrades = (state: RootState) =>
  state.siteConfigReducer.materialGrades;

export const selectLoadingPoints = (state: RootState) =>
  state.siteConfigReducer.loadingPoints;

export const selectDumpingPoints = (state: RootState) =>
  state.siteConfigReducer.dumpingPoints;

export const selectSiteConfigLoaded = (state: RootState) =>
  state.siteConfigReducer.committedSnapshot !== null;

export default siteConfigSlice.reducer;
